// src/pages/AccountSettingsPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Preferences } from "@capacitor/preferences";
import { Check } from "phosphor-react";
import { api } from "api";
import AppHeader from "../components/AppHeader";

/* ---------- iOS-safe auth helpers (same as CreateCirclePage) ---------- */
async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}
  if (typeof window !== "undefined") {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null
    );
  }
  return null;
}

async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function AccountSettingsPage() {
  const navigate = useNavigate();

  // for header
  const [user, setUser] = useState(null);

  // settings state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isProfilePrivate, setIsProfilePrivate] = useState(true);
  const [defaultActivityPrivate, setDefaultActivityPrivate] = useState(true);

  // load /users/me for both header + settings
  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        if (!headers.Authorization) {
          navigate("/login");
          return;
        }
        const meRes = await api.get("/users/me", { headers });
        const me = meRes.data || {};
        setUser(me);
        setEmail(me.email || "");
        setName(me.name || "");
        setUsername(me.username || "");
        setBio(me.bio || "");
        setIsProfilePrivate(
          typeof me.is_profile_private === "boolean"
            ? me.is_profile_private
            : true
        );
        setDefaultActivityPrivate(
          typeof me.default_activity_private === "boolean"
            ? me.default_activity_private
            : true
        );
      } catch (err) {
        console.error("[AccountSettings] failed to load /users/me", err);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleSave = async (e) => {
    e.preventDefault();

    setErrorMsg("");
    setSuccessMsg("");
    setSaving(true);

    const nameClean = (name || "").trim();

    const usernameClean = (username || "")
      .trim()
      .toLowerCase()
      .replace(/^@/, "");

    const bioClean = (bio || "").trim();

    if (!nameClean) {
      setErrorMsg("Display name is required.");
      setSaving(false);
      return;
    }

    if (!/^[a-z0-9._]{3,30}$/.test(usernameClean)) {
      setErrorMsg(
        "Username must be 3–30 characters using lowercase letters, numbers, periods, or underscores."
      );
      setSaving(false);
      return;
    }

    if (bioClean.length > 240) {
      setErrorMsg("Bio cannot exceed 240 characters.");
      setSaving(false);
      return;
    }

    try {
      const headers = await getAuthHeaders();

      const payload = {
        name: nameClean,
        username: usernameClean,
        bio: bioClean,
        is_profile_private: isProfilePrivate,
        default_activity_private: defaultActivityPrivate,
      };

      const res = await api.patch("/users/me", payload, {
        headers,
      });

      const updatedUser = res.data || {};

      setUser(updatedUser);
      setName(updatedUser.name ?? nameClean);
      setUsername(
        updatedUser.username ?? usernameClean
      );
      setBio(updatedUser.bio ?? bioClean);

      setSuccessMsg("Settings saved.");
    } catch (err) {
      console.error("[AccountSettings] save failed", err);

      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to save settings.";

      const normalizedDetail =
        typeof detail === "string"
          ? detail.toLowerCase()
          : "";

      if (
        normalizedDetail.includes(
          "username is already taken"
        )
      ) {
        setErrorMsg("Username is already taken.");
      } else {
        setErrorMsg(
          typeof detail === "string"
            ? detail
            : "Failed to save settings."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user_id");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user_id");
      try {
        await Preferences.remove({ key: "auth_token" });
      } catch {}
    } finally {
      navigate("/login");
    }
  };

  const container = "max-w-7xl mx-auto px-6 md:px-10 xl:px-16";

  /* ------------------------- RENDER (no JSX) ------------------------- */
  if (loading) {
    return React.createElement(
      "div",
      { className: "app-shell bg-blue-50" },
      React.createElement(
        "header",
        { className: "app-header" },
        React.createElement(AppHeader, { user: null })
      ),
      React.createElement(
        "main",
        {
          className: `app-content w-full ${container} pt-[calc(64px+var(--safe-top))] md:mt-4 px-0 py-8`,
        },
        React.createElement(
          "div",
          { className: "text-sm text-gray-500" },
          "Loading settings…"
        )
      )
    );
  }

  return React.createElement(
    "div",
    { className: "app-shell bg-blue-50" },

    // Header
    React.createElement(
      "header",
      { className: "app-header" },
      React.createElement(AppHeader, { user })
    ),

    // Main shell (matches Home / CreateCirclePage)
    React.createElement(
      "main",
      {
        className: `app-content w-full ${container} pt-[calc(64px+var(--safe-top))] md:mt-4 px-0 py-8`,
      },

      React.createElement(
        "div",
        {
          className:
            "grid grid-cols-1 md:grid-cols-[1fr_minmax(0,2fr)_1fr] gap-10 md:overflow-visible",
          style: { height: "auto" },
        },

        // left spacer
        React.createElement("div", { className: "hidden md:block" }),

        // center card
        React.createElement(
          "div",
          { className: "min-w-0" },
          React.createElement(
            "div",
            { className:
              "w-full rounded-[32px] bg-slate-50/80 " +
              "px-4 py-5 shadow-[0_24px_70px_rgba(22,59,86,0.04)] " +
              "sm:p-5 space-y-3" },

            // title
            React.createElement(
              "div",
              { className: "space-y-1" },
              React.createElement(
                "h1",
                {
                  className:
                    "ml-3 dm-font tracking-[-0.03em] text-xl font-bold text-blue-700",
                },
                "Settings"
              ),
              React.createElement(
                "p",
                { className: "ml-3 text-sm text-gray-500" },
                "Manage account, privacy, and sign out"
              )
            ),

            // settings form
            React.createElement(
              "form",
              { onSubmit: handleSave, className: "space-y-5" },

              // Account section
              React.createElement(
                "section",
                {
                  className:
                    "space-y-3.5",
                },

                React.createElement(
                  "h2",
                  {
                    className:
                      "ml-3 text-sm font-semibold text-gray-800",
                  },
                  "Account"
                ),

                // Error message
                errorMsg &&
                  React.createElement(
                    "div",
                    {
                      className:
                        "px-3 pt-1 text-left text-sm font-medium text-red-600",
                      role: "alert",
                      "aria-live": "polite",
                    },
                    errorMsg
                  ),

                // Success message
                successMsg &&
                  React.createElement(
                    "div",
                    {
                      className:
                        "px-3 pt-1 text-left text-sm font-medium text-blue-600",
                      role: "status",
                      "aria-live": "polite",
                    },
                    successMsg
                  ),

                // Display Name
                React.createElement(
                  "div",
                  null,

                  React.createElement(
                    "label",
                    {
                      htmlFor: "settings-display-name",
                      className:
                        "mb-1 ml-3 block text-sm font-semibold text-[#6f665d]",
                    },
                    "Display Name"
                  ),

                  React.createElement("input", {
                    id: "settings-display-name",
                    type: "text",
                    value: name,
                    required: true,
                    maxLength: 50,
                    autoComplete: "name",
                    placeholder: "Jade Kim",

                    onChange: (e) => {
                      setName(e.target.value);
                      setErrorMsg("");
                      setSuccessMsg("");
                    },

                    className:
                      "w-full rounded-3xl border border-white/80 bg-blue-50/85 " +
                      "!min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none " +
                      "placeholder:text-slate-400 focus:border-blue-500 focus:border-2",
                  })
                ),

                // Username
                React.createElement(
                  "div",
                  null,

                  React.createElement(
                    "label",
                    {
                      htmlFor: "settings-username",
                      className:
                        "mb-1 ml-3 block text-sm font-semibold text-[#6f665d]",
                    },
                    "Username"
                  ),

                  React.createElement(
                    "div",
                    { className: "relative" },

                    React.createElement(
                      "span",
                      {
                        className:
                          "pointer-events-none absolute left-3 top-1/2 " +
                          "-translate-y-1/2 text-sm text-slate-400",
                      },
                      "@"
                    ),

                    React.createElement("input", {
                      id: "settings-username",
                      type: "text",
                      value: username,
                      required: true,
                      minLength: 3,
                      maxLength: 30,
                      autoComplete: "username",
                      autoCapitalize: "none",
                      autoCorrect: "off",
                      spellCheck: false,
                      placeholder: "username",

                      onChange: (e) => {
                        const cleaned = e.target.value
                          .toLowerCase()
                          .replace(/^@/, "")
                          .replace(/[^a-z0-9._]/g, "");

                        setUsername(cleaned);
                        setErrorMsg("");
                        setSuccessMsg("");
                      },

                      className:
                        "w-full rounded-3xl border border-white/80 bg-blue-50/85 " +
                        "!min-h-0 !min-w-0 !py-1.5 !pl-6 !pr-3 text-sm outline-none " +
                        "placeholder:text-slate-400 focus:border-blue-500 focus:border-2",
                    })
                  )
                ),

                // Bio
                React.createElement(
                  "div",
                  null,

                  React.createElement(
                    "div",
                    {
                      className:
                        "mb-1 flex items-center justify-between",
                    },

                    React.createElement(
                      "label",
                      {
                        htmlFor: "settings-bio",
                        className:
                          "ml-3 block text-sm font-semibold text-[#6f665d]",
                      },
                      "Bio"
                    ),

                    React.createElement(
                      "span",
                      {
                        className:
                          "mr-4 text-xs text-[#8a8078]",
                      },
                      `${bio.length}/240`
                    )
                  ),

                  React.createElement("textarea", {
                    id: "settings-bio",
                    name: "bio",
                    value: bio,
                    maxLength: 240,
                    rows: 3,
                    placeholder:
                      "Tell people a little about yourself",

                    onChange: (e) => {
                      setBio(e.target.value);
                      setErrorMsg("");
                      setSuccessMsg("");
                    },

                    className:
                      "w-full resize-none rounded-2xl border border-white/80 " +
                      "bg-blue-50/85 !min-h-0 !min-w-0 !px-3 !py-1.5 " +
                      "text-sm outline-none placeholder:text-slate-400 " +
                      "focus:border-blue-500 focus:border-2",
                  })
                ),

                // Email, still read-only
                React.createElement(
                  "div",
                  null,

                  React.createElement(
                    "label",
                    {
                      className:
                        "mb-1 ml-3 block text-sm font-semibold text-[#6f665d]",
                    },
                    "Email"
                  ),

                  React.createElement("input", {
                    type: "email",
                    value: email,
                    readOnly: true,
                    disabled: true,
                    className:
                      "w-full rounded-3xl border border-white/80 bg-blue-50/50 " +
                      "!min-h-0 !min-w-0 !px-3 !py-1.5 text-sm text-gray-500 " +
                      "outline-none disabled:cursor-not-allowed disabled:opacity-70",
                  })
                ),

                // Change password
                React.createElement(
                  "button",
                  {
                    type: "button",
                    onClick: () => navigate("/forgotpassword"),
                    className:
                      "ml-3 !min-h-0 !min-w-0 text-sm font-medium " +
                      "text-blue-600 transition hover:text-blue-700 hover:font-semiold",
                  },
                  "Change password"
                )
              ),

                            // Privacy section
              React.createElement(
                "section",
                { className: "space-y-3" },
                React.createElement(
                  "h2",
                  { className: "ml-3 text-sm font-semibold text-gray-800" },
                  "Privacy"
                ),

                // profile public toggle
                React.createElement(
                  "label",
                  {
                    className:
                      "ml-3 flex items-start gap-2 text-sm text-[#6f665d] cursor-pointer",
                  },
                  React.createElement("input", {
                    type: "checkbox",
                    checked: !isProfilePrivate,
                    onChange: (e) =>
                      setIsProfilePrivate(!e.target.checked),
                    className: "sr-only",
                  }),
                  React.createElement(
                    "div",
                    {
                      className:
                        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-3xl bg-blue-50 border border-slate-200",
                    },
                    React.createElement(Check, {
                      size: 12,
                      weight: "bold",
                      className: `text-blue-500 transition-opacity ${
                        !isProfilePrivate ? "opacity-100" : "opacity-0"
                      }`,
                    })
                  ),
                  React.createElement(
                    "span",
                    null,
                    "Make my profile public",
                    React.createElement("span", {
                      className: "block text-xs text-gray-400",
                      children: "Off = Profile stays private",
                    })
                  )
                ),

                // activity default toggle
                React.createElement(
                  "label",
                  {
                    className:
                      "ml-3 flex items-start gap-2 text-sm text-[#6f665d] cursor-pointer",
                  },
                  React.createElement("input", {
                    type: "checkbox",
                    checked: !defaultActivityPrivate,
                    onChange: (e) =>
                      setDefaultActivityPrivate(!e.target.checked),
                    className: "sr-only",
                  }),
                  React.createElement(
                    "div",
                    {
                      className:
                        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-3xl bg-blue-50 border border-slate-200",
                    },
                    React.createElement(Check, {
                      size: 12,
                      weight: "bold",
                      className: `text-blue-500 transition-opacity ${
                        !defaultActivityPrivate ? "opacity-100" : "opacity-0"
                      }`,
                    })
                  ),
                  React.createElement(
                    "span",
                    null,
                    "Make new activities public by default",
                    React.createElement("span", {
                      className: "block text-xs text-gray-400",
                      children: "Off = Private by default",
                    })
                  )
                )
              ),

              // Session / logout section
              React.createElement(
                "section",
                { className: "ml-3 space-y-2" },
                React.createElement(
                  "h2",
                  { className: "text-sm font-semibold text-gray-800" },
                  "Session"
                ),
                React.createElement(
                  "button",
                  {
                    type: "button",
                    onClick: handleLogout,
                    className: "text-sm text-red-500 hover:font-bold",
                  },
                  "Sign out"
                )
              ),

              // Save button
              React.createElement(
                "div",
                { className: "pt-2" },
                React.createElement(
                  "button",
                  {
                    type: "submit",
                    disabled: saving,
                    className:
                      "w-full bg-blue-600 text-white py-3 rounded-3xl disabled:opacity-50 hover:bg-blue-700 text-sm font-semibold",
                  },
                  saving ? "Saving…" : "Save Changes"
                )
              )
            )
          )
        ),

        // right spacer
        React.createElement("div", { className: "hidden md:block" })
      )
    )
  );
}

export default AccountSettingsPage;